// NANP area code -> US state / Canadian province, for the dial pad's setter-entered leads (SPEC §19, 2026-10-01).
// Shared by the Worker (country/state/zone of a new manual lead) and the web dial pad (live "Georgia" label).
//
// Source: NANPA geographic area code assignments, as tabulated by state/province in Wikipedia "List of North American
// Numbering Plan area codes" (https://en.wikipedia.org/wiki/List_of_North_American_Numbering_Plan_area_codes, read
// 2026-10-01; NANPA: https://www.nationalnanpa.com). Overlays included (e.g. 943 GA 2024, 621 TX, 729 TN 2025,
// 357/738 CA). Codes only reserved or scheduled (italic there: AB 568, NS 851, ON 387/460/487/537/871, QC 273) are left
// out: no number can be in them yet. The Canadian half must agree with CANADIAN_NPAS / regionsOfNpa in
// src/worker/lib/phone.ts (tests/fd-manual-dial.test.ts checks both ways).
import type { Country } from './api';

const US: Record<string, [string, string]> = {
  AL: ['Alabama', '205 251 256 334 483 659 938'],
  AK: ['Alaska', '907'],
  AZ: ['Arizona', '480 520 602 623 928'],
  AR: ['Arkansas', '327 479 501 870'],
  CA: ['California', '209 213 279 310 323 341 350 357 369 408 415 424 442 510 530 559 562 619 626 628 650 657 661 669 707 714 738 747 760 805 818 820 831 837 840 858 909 916 925 949 951'],
  CO: ['Colorado', '303 719 720 748 970 983'],
  CT: ['Connecticut', '203 475 860 959'],
  DE: ['Delaware', '302'],
  DC: ['District of Columbia', '202 771'],
  FL: ['Florida', '239 305 321 324 352 386 407 448 561 645 656 689 727 728 754 772 786 813 850 863 904 941 954'],
  GA: ['Georgia', '229 404 470 478 678 706 762 770 912 943'],
  HI: ['Hawaii', '808'],
  ID: ['Idaho', '208 986'],
  IL: ['Illinois', '217 224 309 312 331 447 464 618 630 708 730 773 779 815 847 861 872'],
  IN: ['Indiana', '219 260 317 463 574 765 812 930'],
  IA: ['Iowa', '319 515 563 641 712'],
  KS: ['Kansas', '316 620 785 913'],
  KY: ['Kentucky', '270 364 502 606 859'],
  LA: ['Louisiana', '225 318 337 457 504 985'],
  ME: ['Maine', '207'],
  MD: ['Maryland', '227 240 301 410 443 667'],
  MA: ['Massachusetts', '339 351 413 508 617 774 781 857 978'],
  MI: ['Michigan', '231 248 269 313 517 586 616 679 734 810 906 947 989'],
  MN: ['Minnesota', '218 320 507 612 651 763 924 952'],
  MS: ['Mississippi', '228 471 601 662 769'],
  MO: ['Missouri', '235 314 417 557 573 636 660 816 975'],
  MT: ['Montana', '406'],
  NE: ['Nebraska', '308 402 531'],
  NV: ['Nevada', '702 725 775'],
  NH: ['New Hampshire', '603'],
  NJ: ['New Jersey', '201 551 609 640 732 848 856 862 908 973'],
  NM: ['New Mexico', '505 575'],
  NY: ['New York', '212 315 329 332 347 363 516 518 585 607 624 631 646 680 716 718 838 845 914 917 929 934'],
  NC: ['North Carolina', '252 336 472 704 743 828 910 919 980 984'],
  ND: ['North Dakota', '701'],
  OH: ['Ohio', '216 220 234 283 326 330 380 419 436 440 513 567 614 740 937'],
  OK: ['Oklahoma', '405 539 572 580 918'],
  OR: ['Oregon', '458 503 541 971'],
  PA: ['Pennsylvania', '215 223 267 272 412 445 484 570 582 610 717 724 814 835 878'],
  RI: ['Rhode Island', '401'],
  SC: ['South Carolina', '803 821 839 843 854 864'],
  SD: ['South Dakota', '605'],
  TN: ['Tennessee', '423 615 629 729 731 865 901 931'],
  TX: ['Texas', '210 214 254 281 325 346 361 409 430 432 469 512 621 682 713 726 737 806 817 830 832 903 915 936 940 945 956 972 979'],
  UT: ['Utah', '385 435 801'],
  VT: ['Vermont', '802'],
  VA: ['Virginia', '276 434 540 571 686 703 757 804 826 948'],
  WA: ['Washington', '206 253 360 425 509 564'],
  WV: ['West Virginia', '304 681'],
  WI: ['Wisconsin', '262 274 353 414 534 608 715 920'],
  WY: ['Wyoming', '307'],
};

const CA: Record<string, [string, string]> = {
  AB: ['Alberta', '368 403 587 780 825'],
  BC: ['British Columbia', '236 250 257 604 672 778'],
  MB: ['Manitoba', '204 431 584'],
  NB: ['New Brunswick', '428 506'],
  NL: ['Newfoundland and Labrador', '709 879'],
  NS: ['Nova Scotia', '782 902'],
  PE: ['Prince Edward Island', '782 902'],
  ON: ['Ontario', '226 249 289 343 365 382 416 437 519 548 613 647 683 705 742 753 807 905 942'],
  QC: ['Quebec', '263 354 367 418 438 450 468 514 579 581 819 873'],
  SK: ['Saskatchewan', '306 474 639'],
  YT: ['Yukon', '867'],
  NT: ['Northwest Territories', '867'],
  NU: ['Nunavut', '867'],
};

export interface AreaCodeRegion {
  country: Country;
  /** Every state/province the code serves, in table order (902/782: NS, PE; 867: YT, NT, NU). */
  regions: string[];
  names: string[];
}

const INDEX: Map<string, AreaCodeRegion> = (() => {
  const m = new Map<string, AreaCodeRegion>();
  const add = (country: Country, table: Record<string, [string, string]>) => {
    for (const [code, [name, list]] of Object.entries(table)) {
      for (const npa of list.split(' ')) {
        const hit = m.get(npa);
        if (hit) { hit.regions.push(code); hit.names.push(name); } else m.set(npa, { country, regions: [code], names: [name] });
      }
    }
  };
  add('US', US);
  add('CA', CA);
  return m;
})();

/** The state(s)/province(s) a geographic US or Canadian area code serves, or null (toll-free, Caribbean, unassigned). */
export function areaCodeRegion(npa: string): AreaCodeRegion | null {
  const r = INDEX.get(npa);
  return r ? { country: r.country, regions: [...r.regions], names: [...r.names] } : null;
}

/** Every area code in the table, for tests. */
export function allAreaCodes(): string[] {
  return [...INDEX.keys()];
}
